import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Flex,
    Text,
    Button,
    Select,
    Input,
    HStack,
    VStack,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Checkbox,
    Tag,
    TagLabel,
    TagCloseButton,
    RadioGroup,
    Radio,
    Avatar,
    UnorderedList,
    ListItem,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import PageTitle from '../components/PageTitle';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';
import { POSITIONS, LEVELS, STAT_TYPES } from '../constants';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useTeamRoster } from '../hooks/useTeamRoster';
import { getTeamInfoById, getTeamNameById } from '../utils/getTeamInfo';
import { showPlayerNotes } from '../utils/showPlayerNotes';
import { formatSalary } from '../utils/formatMoney';
import { formatPositionsList } from '../utils/formatPositionsList';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

/** POS column: bucket arrays from research row + `rwPlayer.seasonPositions`; empty → N/A (not em dash). */
function researchPositionsDisplay(row: {
    primaryPosition?: string[];
    secondaryPosition?: string[];
    tertiaryPosition?: string[];
    rwPlayer?: {
        seasonPositions?: {
            primaryPosition?: string[] | null;
            secondaryPosition?: string[] | null;
            tertiaryPosition?: string[] | null;
        } | null;
    };
}): string {
    const sp = row.rwPlayer?.seasonPositions;
    const raw = formatPositionsList({
        primaryPosition: (row.primaryPosition ?? sp?.primaryPosition) ?? undefined,
        secondaryPosition: (row.secondaryPosition ?? sp?.secondaryPosition) ?? undefined,
        tertiaryPosition: (row.tertiaryPosition ?? sp?.tertiaryPosition) ?? undefined,
    });
    return raw === '—' ? 'N/A' : raw;
}

type Chip = { 
    type: 'search' | 'position' | 'level' | 'team'; 
    label: string; 
    value?: string; 
};

interface PlayerData {
    id: string;
    full_name: string;
    mlb_team: string;
    position: string;
    age: number;
    level: string;
    club: string;
    [key: string]: unknown; // For dynamic salary fields like salary_2024, salary_2025, etc.
}

// Extended interface to handle the actual myLeague structure
interface ExtendedMyLeague {
        conferences: {
            id: number;
            name: string;
            divisions: {
                id: number;
                name: string;
                teams: {
                    id: number;
                    name: string;
                }[];
            }[];
        }[];
}

const columnHelper = createColumnHelper<PlayerData>();

const PAGE_SIZE = 100;

const ResearchPage = () => {
    // Get league data from store
    const myLeague = useMyLeagueStore(state => state.myLeague);
    const leagueId = myLeague?.id;
    
    // Get searchForPlayers function from hook
    const { searchForPlayers } = useTeamRoster(1); // teamId not used for research
    const searchForPlayersRef = useRef(searchForPlayers);
    useEffect(() => {
        searchForPlayersRef.current = searchForPlayers;
    }, [searchForPlayers]);
    
    // Filter state - updated to match Free Agents pattern
    const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
    const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
    const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
    const [search, setSearch] = useState<string>('');
    const [statType, setStatType] = useState('salaries');
    const [tableData, setTableData] = useState<PlayerData[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const loadingRef = useRef(false); // Prevent multiple simultaneous calls
    // Using current year for salary data
    const currentYear = new Date().getFullYear();
    
    // Popover state
    const [positionPopoverOpen, setPositionPopoverOpen] = useState(false);
    const [levelPopoverOpen, setLevelPopoverOpen] = useState(false);
    const [teamPopoverOpen, setTeamPopoverOpen] = useState(false);

    const positionsForApi = useMemo(() => {
        if (selectedPositions.length === 0) {
            return [];
        }

        const expanded = new Set<string>();
        selectedPositions.forEach((pos) => {
            if (pos === 'IF') {
                ['1B', '2B', '3B', 'SS', 'INF'].forEach((p) => expanded.add(p));
            } else if (pos === 'OF') {
                ['LF', 'CF', 'RF', 'OF', 'LRF'].forEach((p) => expanded.add(p));
            } else {
                expanded.add(pos);
            }
        });

        return Array.from(expanded);
    }, [selectedPositions]);

    const levelsForApi = useMemo(() => {
        if (selectedLevels.length === 0) {
            return [];
        }

        const expanded = new Set<string>();
        selectedLevels.forEach((level) => {
            if (level === 'R') {
                expanded.add('R');
                expanded.add('RA');
            } else {
                expanded.add(level);
            }
        });

        return Array.from(expanded);
    }, [selectedLevels]);

    const activeFilters = useMemo(
        () => ({
            search: search.trim(),
            positions: positionsForApi,
            levels: levelsForApi,
            teams: selectedTeams,
        }),
        [search, positionsForApi, levelsForApi, selectedTeams]
    );

    const filterKey = useMemo(
        () =>
            JSON.stringify({
                search: activeFilters.search.toLowerCase(),
                positions: [...activeFilters.positions].sort(),
                levels: [...activeFilters.levels].sort(),
                teams: [...activeFilters.teams].sort(),
            }),
        [activeFilters]
    );

    useEffect(() => {
        setCurrentPage(0);
        setTableData([]);
        setTotalCount(0);
    }, [leagueId, filterKey]);

    useEffect(() => {
        if (!leagueId) return;

        let isCancelled = false;

        const fetchPlayers = async () => {
            const isFirstPage = currentPage === 0;

            if (isFirstPage) {
                setLoading(true);
                setError(null);
            } else {
                setIsFetchingMore(true);
            }

            loadingRef.current = true;

            try {
                const filters: Record<string, any> = {
                    page: currentPage,
                    rowsPerPage: PAGE_SIZE,
                };

                if (activeFilters.search) {
                    filters.name = activeFilters.search;
                }
                if (activeFilters.positions.length > 0) {
                    filters.positions = activeFilters.positions;
                }
                if (activeFilters.levels.length > 0) {
                    filters.levels = activeFilters.levels;
                }
                if (activeFilters.teams.length > 0) {
                    filters.teams = activeFilters.teams;
                }

                const result = await searchForPlayersRef.current(leagueId, filters);

                if (isCancelled) return;

                setTotalCount(result.totalCount || 0);
                setTableData(prev =>
                    isFirstPage ? result.players : [...prev, ...(result.players || [])]
                );
            } catch (err) {
                if (isCancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load players');
            } finally {
                if (!isCancelled) {
                    if (currentPage === 0) {
                        setLoading(false);
                    } else {
                        setIsFetchingMore(false);
                    }
                    loadingRef.current = false;
                }
            }
        };

        fetchPlayers();

        return () => {
            isCancelled = true;
        };
    }, [leagueId, currentPage, filterKey]);

    const filteredPlayers = tableData;

    // Table columns for react-table
    const columns = useMemo(() => {
        return [
            columnHelper.accessor('rwPlayer', { 
                header: 'Player',
                cell: info => {
                    const player = info.getValue() as {
                        image?: string;
                        first_name?: string;
                        last_name?: string;
                    };
                    const teamInfo = getTeamInfoById(info.row.original.team_id, myLeague);
                    const headshotUrl = httpsPlayerImageUrl(player?.image);
                    return (
                        <Flex align="center">
                            {headshotUrl ? (
                                <Box 
                                    w="40px" 
                                    h="40px" 
                                    borderRadius="full" 
                                    overflow="hidden" 
                                    mr={2}
                                    bg="transparent"
                                >
                                    <img 
                                        src={headshotUrl} 
                                        alt={`${player.first_name} ${player.last_name}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Avatar 
                                    size="md" 
                                    name={`${player?.first_name} ${player?.last_name}`} 
                                    mr={2}
                                    bg={teamInfo?.color_primary || "gray.600"}
                                    color={teamInfo?.color_text || "white"}
                                    border={`2px solid ${teamInfo?.color_secondary || 'transparent'}`}
                                />
                            )}
                            {player?.first_name} {player?.last_name}
                        </Flex>
                    );
                }
            }),
            columnHelper.accessor('team_id', { 
                header: 'Team',
                cell: info => {
                    const teamId = info.getValue();
                    if (!teamId) return 'Free Agent';
                    const teamName = getTeamNameById(Number(teamId), myLeague);
                    return teamName || `Team ${teamId}`;
                }
            }),
            columnHelper.accessor('level', { 
              header: 'Level',
              cell: info => info.getValue() || 'N/A'
          }),
            columnHelper.accessor('rwPlayer.position_1', { 
                header: 'POS (B/T)',
                size: 100,
                cell: info => {
                    const positionsText = researchPositionsDisplay(
                        info.row.original as Parameters<typeof researchPositionsDisplay>[0]
                    );
                    const bats = info.row.original.rwPlayer?.bats || '-';
                    const throws = info.row.original.rwPlayer?.throws || '-';
                    const btText = `${bats}/${throws}`;
                    
                    return (
                        <VStack align="flex-start" spacing={1}>
                            <Text>{positionsText}</Text>
                            <Text fontSize="xs" color="gray.400">{btText}</Text>
                        </VStack>
                    );
                }
            }),


            columnHelper.accessor('rwPlayer.dob', { 
              header: 'Age',
              cell:info => {
                const dob = info.getValue() || null
                if (dob) {
                  const age = Math.floor((Date.now() - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25));
                  return age;
                }
                return '';
              }
          }),
            columnHelper.accessor('playerContract.current_salary', { 
                header: 'Salary',
                cell: info => {
                    const salary = info.getValue();
                    return salary ? `${formatSalary(salary, false)}` : '-';
                }
            }),
            columnHelper.accessor('status', { 
                header: 'Notes',
                cell: info => {
                    const player = info.row.original;
                    const notes = showPlayerNotes(player, () => {}); // Empty function since no deal click needed
                    if (notes.length === 0) {
                        return <Text fontSize="sm" color="gray.400">-</Text>;
                    }
                    
                    return (
                        <UnorderedList spacing={1} fontSize="xs" color="gray.300">
                            {notes.map((note, index) => (
                                <ListItem key={index}>
                                    {typeof note === 'string' ? note : note}
                                </ListItem>
                            ))}
                        </UnorderedList>
                    );
                }
            })
        ];
    }, [currentYear, myLeague]);

    const table = useReactTable({
        data: filteredPlayers,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    // performSearch function removed - using default search on page load only

    // Position multi-select logic
    const togglePosition = (pos: string) => {
        setSelectedPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
    };

    // Level multi-select logic  
    const toggleLevel = (level: string) => {
        setSelectedLevels(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]);
    };

    // Team multi-select logic
    const toggleTeam = (team: string) => {
        setSelectedTeams(prev => prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]);
    };

    // Remove a filter chip
    const removeChip = (type: string, value?: string) => {
        if (type === 'search') setSearch('');
        if (type === 'position' && value) setSelectedPositions(selectedPositions.filter(p => p !== value));
        if (type === 'level' && value) setSelectedLevels(selectedLevels.filter(l => l !== value));
        if (type === 'team' && value) setSelectedTeams(selectedTeams.filter(t => t !== value));
    };

    const handleLoadMore = () => {
        if (loadingRef.current) return;
        if (tableData.length >= totalCount) return;
        setCurrentPage(prev => prev + 1);
    };

    // Render filter chips
    const chips: Chip[] = [
        search ? { type: 'search', label: `Search: ${search}` } : undefined,
        ...selectedPositions.map(pos => ({ 
            type: 'position', 
            label: POSITIONS.find(p => p.value === pos)?.label || pos, 
            value: pos 
        })),
        ...selectedLevels.map(level => ({ 
            type: 'level', 
            label: LEVELS.find(l => l.value === level)?.label || level, 
            value: level 
        })),
        ...selectedTeams.map(teamId => ({ 
            type: 'team', 
            label: getTeamNameById(Number(teamId), myLeague) || `Team ${teamId}`, 
            value: teamId 
        })),
    ].filter((chip): chip is Chip => Boolean(chip));

    return (
        <Box p={8}>
            <PageTitle mb={4}>Research Players</PageTitle>
            
            {/* Filter Bar */}
            <VStack align="flex-start" spacing={4} mb={2}>
                <HStack spacing={4} flexWrap="wrap">
                    {/* Year selector removed - using current league data */}

                    {/* Search Input */}
                    <Input
                        placeholder="Search by name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        width="220px"
                        bg="whiteAlpha.100"
                        color="white"
                        _placeholder={{ color: 'gray.400' }}
                    />

                    {/* Position Multi-Select */}
                    <Popover
                        isOpen={positionPopoverOpen}
                        onClose={() => setPositionPopoverOpen(false)}
                        placement="bottom-start"
                    >
                        <PopoverTrigger>
                            <Button onClick={() => setPositionPopoverOpen(!positionPopoverOpen)} variant="outline" colorScheme="blue">
                                {selectedPositions.length > 0 ? `${selectedPositions.length} Position${selectedPositions.length > 1 ? 's' : ''}` : 'Select Positions'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="gray.800" color="white" minW="200px">
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverBody>
                                <VStack align="flex-start" spacing={1}>
                                    {POSITIONS.map(pos => (
                                        <Checkbox
                                            key={pos.value}
                                            isChecked={selectedPositions.includes(pos.value)}
                                            onChange={() => togglePosition(pos.value)}
                                            colorScheme="blue"
                                        >
                                            {pos.label}
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>

                    {/* Level Multi-Select */}
                    <Popover
                        isOpen={levelPopoverOpen}
                        onClose={() => setLevelPopoverOpen(false)}
                        placement="bottom-start"
                    >
                        <PopoverTrigger>
                            <Button onClick={() => setLevelPopoverOpen(!levelPopoverOpen)} variant="outline" colorScheme="blue">
                                {selectedLevels.length > 0 ? `${selectedLevels.length} Level${selectedLevels.length > 1 ? 's' : ''}` : 'Select Levels'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="gray.800" color="white" minW="200px">
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverBody>
                                <VStack align="flex-start" spacing={1}>
                                    {LEVELS.map(level => (
                                        <Checkbox
                                            key={level.value}
                                            isChecked={selectedLevels.includes(level.value)}
                                            onChange={() => toggleLevel(level.value)}
                                            colorScheme="blue"
                                        >
                                            {level.label}
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>

                    {/* Team Multi-Select */}
                    <Popover
                        isOpen={teamPopoverOpen}
                        onClose={() => setTeamPopoverOpen(false)}
                        placement="bottom-start"
                    >
                        <PopoverTrigger>
                            <Button onClick={() => setTeamPopoverOpen(!teamPopoverOpen)} variant="outline" colorScheme="blue">
                                {selectedTeams.length > 0 ? `${selectedTeams.length} Team${selectedTeams.length > 1 ? 's' : ''}` : 'Select Teams'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="gray.800" color="white" minW="300px" maxH="400px" overflowY="auto">
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverBody>
                                <VStack align="flex-start" spacing={3}>
                                    {(myLeague as ExtendedMyLeague)?.conferences ? (
                                        (myLeague as ExtendedMyLeague)!.conferences.map(conference => (
                                            <Box key={conference.id} w="full">
                                                <Text fontWeight="bold" color="blue.300" mb={2} fontSize="sm">
                                                    {conference.name}
                                                </Text>
                                                {conference.divisions.map(division => (
                                                    <Box key={division.id} ml={2} mb={2}>
                                                        <Text fontWeight="semibold" color="gray.300" mb={1} fontSize="xs">
                                                            {division.name}
                                                        </Text>
                                                        <VStack align="flex-start" spacing={1} ml={2}>
                                                            {division.teams.map(team => (
                                                                <Checkbox
                                                                    key={team.id}
                                                                    isChecked={selectedTeams.includes(team.id.toString())}
                                                                    onChange={() => toggleTeam(team.id.toString())}
                                                                    colorScheme="blue"
                                                                    size="sm"
                                                                >
                                                                    <Text fontSize="xs">{team.name}</Text>
                                                                </Checkbox>
                                                            ))}
                                                        </VStack>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))
                                    ) : (
                                        <Text color="gray.400">No league data available</Text>
                                    )}
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>

                    {/* Search button removed - using default search on page load */}
                </HStack>

                {/* Filter Chips */}
                <HStack spacing={2} flexWrap="wrap">
                    {chips.map((chip, idx) => (
                        <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
                            <TagLabel>{chip.label}</TagLabel>
                            <TagCloseButton onClick={() => removeChip(chip.type, chip.value)} />
                        </Tag>
                    ))}
                </HStack>
            </VStack>

            {/* Stats Type Radio Buttons */}
            <Flex align="center" gap={4} mb={4} mt={8}>
                <Text>Show:</Text>
                <RadioGroup
                    value={statType}
                    onChange={setStatType}
                    colorScheme="blue"
                    display="flex"
                    flexDir="row"
                    gap={4}
                >
                    {STAT_TYPES.map((type) => (
                        <Radio key={type.value} value={type.value} colorScheme="blue">
                            {type.label}
                        </Radio>
                    ))}
                </RadioGroup>
            </Flex>

            {/* Loading State */}
            {loading && (
                <Text color="blue.400" mb={2} fontWeight="medium">
                    Loading players...
                </Text>
            )}

            {/* Error State */}
            {error && (
                <Text color="red.400" mb={2} fontWeight="medium">
                    Error: {error}
                </Text>
            )}

            {/* Player Count */}
            {!loading && !error && (
                <Text color="blue.400" mb={2} fontWeight="medium">
                    Showing {filteredPlayers.length} of {totalCount || filteredPlayers.length} players
                </Text>
            )}

            {/* Table */}
            {!loading && !error && (
                <Box overflowX="auto" bg="whiteAlpha.100" borderRadius="md" boxShadow="sm">
                <table style={{ width: '100%', borderCollapse: 'collapse', color: 'white' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.1)' }}>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        style={{
                                            padding: '12px',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            color: 'white',
                                            width: header.column.getSize() ? `${header.column.getSize()}px` : 'auto',
                                            minWidth: header.column.getSize() ? `${header.column.getSize()}px` : 'auto',
                                            maxWidth: header.column.getSize() ? `${header.column.getSize()}px` : 'auto',
                                        }}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: 'center', color: 'gray.400', padding: '16px' }}>
                                    No players found
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} style={{ 
                                            padding: '12px', 
                                            textAlign: 'left', 
                                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                                            width: cell.column.getSize() ? `${cell.column.getSize()}px` : 'auto',
                                            minWidth: cell.column.getSize() ? `${cell.column.getSize()}px` : 'auto',
                                            maxWidth: cell.column.getSize() ? `${cell.column.getSize()}px` : 'auto',
                                        }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </Box>
            )}

            {!loading && !error && filteredPlayers.length < totalCount && (
                <Flex justify="center" mt={4}>
                    <Button
                        colorScheme="blue"
                        onClick={handleLoadMore}
                        isLoading={isFetchingMore}
                        isDisabled={loadingRef.current}
                    >
                        Load More
                    </Button>
                </Flex>
            )}
        </Box>
    );
};

export default ResearchPage; 